import React from 'react'
import {
	CardFooter,
	FooterActions,
	FooterHint,
	GhostButton,
	PrimaryButton,
} from '../ItineraryImportPage.styles'
import { PageState } from '../types'
import { SparkleGlyph } from './icons'

interface ImportFooterProps {
	pageState: PageState
	submitDisabled: boolean
	isPending: boolean
	hasValidationErrors: boolean
	validationErrorCount: number
	onCancel: () => void
	onSubmit: () => void
	onImportMore: () => void
	onViewTimeline: () => void
	onTryAgain: () => void
	onRetry: () => void
}

const ImportFooter: React.FC<ImportFooterProps> = ({
	pageState,
	submitDisabled,
	isPending,
	hasValidationErrors,
	validationErrorCount,
	onCancel,
	onSubmit,
	onImportMore,
	onViewTimeline,
	onTryAgain,
	onRetry,
}) => {
	if (pageState === 'idle') {
		return (
			<CardFooter>
				<FooterHint>
					{hasValidationErrors && (
						<>
							Remove the {validationErrorCount} flagged file
							{validationErrorCount === 1 ? '' : 's'} to continue.
						</>
					)}
				</FooterHint>
				<FooterActions>
					<GhostButton onClick={onCancel}>Cancel</GhostButton>
					<PrimaryButton
						$variant="cool"
						$disabled={submitDisabled}
						disabled={submitDisabled}
						onClick={onSubmit}
					>
						<SparkleGlyph size={14} />
						{isPending ? 'Starting…' : 'Build itinerary'}
					</PrimaryButton>
				</FooterActions>
			</CardFooter>
		)
	}
	if (pageState === 'processing') {
		return (
			<CardFooter>
				<FooterHint>You can leave this page — we'll keep working in the background.</FooterHint>
				<FooterActions>
					<GhostButton onClick={onCancel}>Back to trip</GhostButton>
				</FooterActions>
			</CardFooter>
		)
	}
	if (pageState === 'success') {
		return (
			<CardFooter>
				<FooterHint>You can edit these events anytime in your itinerary.</FooterHint>
				<FooterActions>
					<GhostButton onClick={onImportMore}>Import more</GhostButton>
					<PrimaryButton $variant="warm" onClick={onViewTimeline}>
						View on timeline →
					</PrimaryButton>
				</FooterActions>
			</CardFooter>
		)
	}
	if (pageState === 'no_events') {
		return (
			<CardFooter>
				<FooterHint>Nothing was added to your trip.</FooterHint>
				<FooterActions>
					<GhostButton onClick={onCancel}>Cancel</GhostButton>
					<PrimaryButton $variant="cool" onClick={onTryAgain}>
						Try a different file
					</PrimaryButton>
				</FooterActions>
			</CardFooter>
		)
	}
	if (pageState === 'failed') {
		return (
			<CardFooter>
				<FooterHint>If this keeps happening, drop us a line in Settings → Help.</FooterHint>
				<FooterActions>
					<GhostButton onClick={onCancel}>Cancel</GhostButton>
					<PrimaryButton $variant="warm" onClick={onRetry}>
						Try again
					</PrimaryButton>
				</FooterActions>
			</CardFooter>
		)
	}
	if (pageState === 'rate_limit') {
		return (
			<CardFooter>
				<FooterHint>Limit is per trip per day. Other trips aren't affected.</FooterHint>
				<FooterActions>
					<GhostButton onClick={onCancel}>Close</GhostButton>
				</FooterActions>
			</CardFooter>
		)
	}
	if (pageState === 'stuck') {
		return (
			<CardFooter>
				<FooterHint>Your source files are saved — no need to re-upload.</FooterHint>
				<FooterActions>
					<GhostButton onClick={onCancel}>Cancel</GhostButton>
					<PrimaryButton $variant="warm" onClick={onRetry}>
						Retry import
					</PrimaryButton>
				</FooterActions>
			</CardFooter>
		)
	}
	return null
}

export default ImportFooter
